import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView } from 'react-native';
import { Controller, FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Select } from 'app/design/primitives/select';
import { Modal } from 'app/design/primitives/modal';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Switch } from 'app/design/primitives/switch';
import { Text } from 'app/design/primitives/text';
import { useForm, useFormContext } from 'app/hooks/form';
import { useNavigationOptions } from 'app/hooks/navigation';

import type { BatchUpdateListingLoanFeeDTO } from 'app/network/listing';
import { useLoanFeeForListings, useManyListing } from 'app/network/listing';
import { ListingType } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import Responsive from 'app/components/responsive';
import { SelectPickerHtml } from 'app/design/primitives/select/select-html';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { schema } from './loan-fee.schema';

export { schema } from './loan-fee.schema';

const theme = createDSTheme();

const LoanFeeScreenComponent: NavigationScreen<'loan-fee'> = ({ navigation, route }) => {
  const { t } = useTranslation('loanFee');
  const listingIds = route.params.listingId.split(',');
  const { data: listings, isFetchedAfterMount: listingsFetched } = useManyListing(listingIds, 'loan-fee');
  const { mutate: saveLoanFee, isPending: isSaving } = useLoanFeeForListings(listingIds);

  const form = useForm({ schema });
  const { watch, setValue } = form;

  const watchLoanFee = watch('loanFee');
  const watchLoanFeeDescription = watch('loanFeeDescription');
  const watchLoanFeePaymentMethod = watch('loanFeePaymentMethod');

  const firstListingWithLoanFee = !listingsFetched ? undefined : listings?.find((listing) => listing.loanFee);

  useEffect(() => {
    if (firstListingWithLoanFee) {
      setValue('loanFee', firstListingWithLoanFee.loanFee);
      setValue('loanFeeDescription', firstListingWithLoanFee.loanFeeDescription);
      setValue('loanFeePaymentMethod', firstListingWithLoanFee.loanFeePaymentMethod);
    }
  }, [firstListingWithLoanFee, setValue]);

  const saveForm = () => {
    saveLoanFee(form.getValues(), {
      onSuccess: () =>
        navigation.navigate(
          'main',
          {
            screen: 'management',
            params: {
              screen: 'my-listings',
              params: { type: ListingType.offer, isSelecting: false },
            },
          },
          { pop: true },
        ),
    });
  };

  const isDirty = (() => {
    return !(
      listings?.every((l) => l.loanFee === watchLoanFee) &&
      listings.every((l) => l.loanFeeDescription === watchLoanFeeDescription) &&
      listings.every((l) => l.loanFeePaymentMethod === watchLoanFeePaymentMethod)
    );
  })();

  useNavigationOptions(navigation, {
    headerBackVisible: false,
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {listingIds.length > 1 ? t('selectionTitle', { count: listingIds.length }) : t('title')}
      </Text>
    ),
  });
  const setPageTitle = usePageTitle('loanFee');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const loanFeeModalsState = useLoanFeeModals();

  return (
    <Page
      accessory={
        <Accessory style={{ marginHorizontal: theme.spacing['spacing.general.x4'] }}>
          <Button disabled={!isDirty} loading={isSaving} onPress={saveForm} variant="primary">
            {t('saveButton')}
          </Button>
        </Accessory>
      }
      isModal
    >
      <FormProvider {...form}>
        <LoanFeeFormFields {...{ loanFeeEnabled: true, modalsState: loanFeeModalsState }} />
      </FormProvider>
      <LoanFeeModals state={loanFeeModalsState} />
    </Page>
  );
};

const LoanFeeScreenWithErrorBoundary = withErrorBoundary(LoanFeeScreenComponent);

export const LoanFeeScreen: NavigationScreen<'loan-fee'> = (props) => <LoanFeeScreenWithErrorBoundary {...props} />;

export interface LoanFeeModalsState {
  isLoanFeeModalOpen: boolean;
  setIsLoanFeeModalOpen: Dispatch<SetStateAction<boolean>>;
  isLoanFeeFeeModalOpen: boolean;
  setIsLoanFeeFeeModalOpen: Dispatch<SetStateAction<boolean>>;
  isLoanFeeMethodModalOpen: boolean;
  setIsLoanFeeMethodModalOpen: Dispatch<SetStateAction<boolean>>;
  isLoanFeeOtherModalOpen: boolean;
  setIsLoanFeeOtherModalOpen: Dispatch<SetStateAction<boolean>>;
}

export function useLoanFeeModals(): LoanFeeModalsState {
  const [isLoanFeeModalOpen, setIsLoanFeeModalOpen] = useState<boolean>(false);
  const [isLoanFeeFeeModalOpen, setIsLoanFeeFeeModalOpen] = useState<boolean>(false);
  const [isLoanFeeMethodModalOpen, setIsLoanFeeMethodModalOpen] = useState<boolean>(false);
  const [isLoanFeeOtherModalOpen, setIsLoanFeeOtherModalOpen] = useState<boolean>(false);
  return {
    isLoanFeeModalOpen,
    setIsLoanFeeModalOpen,
    isLoanFeeFeeModalOpen,
    setIsLoanFeeFeeModalOpen,
    isLoanFeeMethodModalOpen,
    setIsLoanFeeMethodModalOpen,
    isLoanFeeOtherModalOpen,
    setIsLoanFeeOtherModalOpen,
  };
}

export function LoanFeeFormFields({
  modalsState,
  loanFeeEnabled,
  setLoanFeeEnabled,
}: {
  modalsState: LoanFeeModalsState;
  loanFeeEnabled: boolean;
  setLoanFeeEnabled?: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation('loanFee');

  const { control, setValue } = useFormContext<BatchUpdateListingLoanFeeDTO>();
  const { isLargeUI } = useUISize();

  const handleLoanFeeChange = (value: string | null) => {
    if (isLargeUI) {
      const numericValue = value?.replace(/[^0-9]/g, '') || '';
      setValue('loanFee', numericValue ? Number(numericValue) : null);
    }
  };

  return (
    <Column
      gap="spacing.general.x4"
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        padding: theme.spacing['spacing.general.x4'],
        backgroundColor: theme.colors['surface.default'],
      }}
    >
      {setLoanFeeEnabled ? (
        <Row alignItems="center" justifyContent="space-between">
          <Row alignItems="center" gap="spacing.general.x2">
            <Text>{t('fields.loanFeePrompt')}</Text>
            <Pressable onPress={() => modalsState.setIsLoanFeeModalOpen(true)}>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
            </Pressable>
          </Row>
          <Switch
            onValueChange={(on) => {
              setLoanFeeEnabled(on);
              if (!on) {
                setValue('loanFee', null);
                setValue('loanFeeDescription', null);
                setValue('loanFeePaymentMethod', null);
              }
            }}
            value={loanFeeEnabled}
          />
        </Row>
      ) : null}
      {loanFeeEnabled ? (
        <Column gap="spacing.general.x4">
          <ControlledInput
            {...{ control }}
            backgroundColor={theme.colors['surface.default']}
            label={t('fields.loanFeeDescriptionLabel')}
            labelRight={
              setLoanFeeEnabled ? null : (
                <Pressable onPress={() => modalsState.setIsLoanFeeModalOpen(true)}>
                  <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
                </Pressable>
              )
            }
            multiline
            name="loanFeeDescription"
            nullable
            placeholder={t('fields.loanFeeDescriptionPlaceholder')}
            scrollEnabled={false} // Required for multiline input to scroll into view, when given focus and the keyboard is shown
          />
          <ControlledInput
            {...{ control }}
            backgroundColor={theme.colors['surface.default']}
            keyboardType="numeric"
            label={t('fields.loanFeeLabel')}
            labelRight={
              <Pressable onPress={() => modalsState.setIsLoanFeeFeeModalOpen(true)}>
                <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
              </Pressable>
            }
            left={<Text variant="bodyBase">$</Text>}
            name="loanFee"
            nullable
            placeholder={t('fields.loanFeePlaceholder')}
            onChangeText={handleLoanFeeChange}
          />
          <Controller
            {...{ control }}
            name="loanFeePaymentMethod"
            render={({ field, fieldState }) => {
              return (
                <Responsive
                  desktop={
                    <SelectPickerHtml
                      data={[
                        {
                          key: 'cash',
                          value: t('paymentMethods.cash'),
                        },
                        {
                          key: 'interac',
                          value: t('paymentMethods.interac'),
                        },
                        {
                          key: 'eft',
                          value: t('paymentMethods.eft'),
                        },
                        {
                          key: 'other',
                          value: t('paymentMethods.other'),
                        },
                      ]}
                      defaultOption={{
                        key: field.value || '',
                        value:
                          field.value === 'cash'
                            ? t('paymentMethods.cash')
                            : field.value === 'interac'
                              ? t('paymentMethods.interac')
                              : field.value === 'eft'
                                ? t('paymentMethods.eft')
                                : field.value === 'other'
                                  ? t('paymentMethods.other')
                                  : '',
                      }}
                      setSelected={(optionID) => {
                        field.onChange(optionID);
                      }}
                      placeholder={t('fields.loanFeePaymentMethodLabel')}
                      maxHeight={300}
                    />
                  }
                  mobile={
                    <Select
                      allowSearch={false}
                      backgroundColor={theme.colors['surface.default']}
                      error={fieldState.error}
                      label={t('fields.loanFeePaymentMethodLabel')}
                      labelRight={
                        <Pressable onPress={() => modalsState.setIsLoanFeeMethodModalOpen(true)}>
                          <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
                        </Pressable>
                      }
                      onChange={field.onChange}
                      options={[
                        {
                          label: t('fields.loanFeePaymentMethodLabel'),
                          values: [
                            {
                              label: t('paymentMethods.cash'),
                              value: 'cash',
                            },
                            {
                              label: t('paymentMethods.interac'),
                              value: 'interac',
                            },
                            {
                              label: t('paymentMethods.eft'),
                              value: 'eft',
                            },
                            {
                              label: t('paymentMethods.other'),
                              value: 'other',
                            },
                          ],
                        },
                      ]}
                      value={field.value}
                    />
                  }
                />
              );
            }}
          />
        </Column>
      ) : null}
    </Column>
  );
}

export function LoanFeeModals({ state }: { state: LoanFeeModalsState }) {
  const { t } = useTranslation('loanFee');
  const {
    isLoanFeeModalOpen,
    setIsLoanFeeModalOpen,
    isLoanFeeFeeModalOpen,
    setIsLoanFeeFeeModalOpen,
    isLoanFeeMethodModalOpen,
    setIsLoanFeeMethodModalOpen,
    isLoanFeeOtherModalOpen,
    setIsLoanFeeOtherModalOpen,
  } = state;
  return (
    <>
      <Modal onClose={() => setIsLoanFeeModalOpen(false)} open={isLoanFeeModalOpen} transparent>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setIsLoanFeeModalOpen(false)}>
            {t('modals.loanFee.title')}
          </Modal.Title>

          <Column paddingHorizontal="spacing.general.x6">
            <ScrollView>
              <Text variant="bodyLarge">
                <Trans
                  components={{
                    strong: <Text fontWeight="700" />,
                  }}
                  i18nKey="modals.loanFee.text"
                  ns="loanFee"
                />
                {'\n\n'}
                <Trans
                  components={{
                    strong: <Text fontWeight="700" />,
                  }}
                  i18nKey="modals.loanFee.detailsForLender"
                  ns="loanFee"
                />
              </Text>
            </ScrollView>
          </Column>
        </Modal.Card>
      </Modal>

      <Modal onClose={() => setIsLoanFeeFeeModalOpen(false)} open={isLoanFeeFeeModalOpen} transparent>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setIsLoanFeeFeeModalOpen(false)}>
            {t('modals.amount.title')}
          </Modal.Title>

          <Column paddingHorizontal="spacing.general.x6">
            <ScrollView>
              <Text variant="bodyLarge">
                <Trans
                  components={{
                    strong: <Text fontWeight="700" />,
                  }}
                  i18nKey="modals.amount.text"
                  ns="loanFee"
                />
              </Text>
            </ScrollView>
          </Column>
        </Modal.Card>
      </Modal>

      <Modal onClose={() => setIsLoanFeeMethodModalOpen(false)} open={isLoanFeeMethodModalOpen} transparent>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setIsLoanFeeMethodModalOpen(false)}>
            {t('modals.paymentMethod.title')}
          </Modal.Title>

          <Column paddingHorizontal="spacing.general.x6">
            <ScrollView>
              <Text variant="bodyLarge">
                <Trans
                  components={{
                    strong: <Text fontWeight="700" />,
                  }}
                  i18nKey="modals.paymentMethod.text"
                  ns="loanFee"
                />
              </Text>
            </ScrollView>
          </Column>
        </Modal.Card>
      </Modal>

      <Modal onClose={() => setIsLoanFeeOtherModalOpen(false)} open={isLoanFeeOtherModalOpen} transparent>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setIsLoanFeeOtherModalOpen(false)}>
            {t('modals.other.title')}
          </Modal.Title>

          <Column paddingHorizontal="spacing.general.x6">
            <ScrollView>
              <Text variant="bodyLarge">
                <Trans
                  components={{
                    strong: <Text fontWeight="700" />,
                  }}
                  i18nKey="modals.loanFee.text"
                  ns="loanFee"
                />
                {'\n\n'}
                <Trans
                  components={{
                    strong: <Text fontWeight="700" />,
                  }}
                  i18nKey="modals.paymentMethod.text"
                  ns="loanFee"
                />
              </Text>
            </ScrollView>
          </Column>
        </Modal.Card>
      </Modal>
    </>
  );
}
